import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import type {
  AuthResponse,
  UserCreateDTO,
  UserLoginDTO,
  UserResponseDTO,
  SaveResponseDTO,
  SaveListResponseDTO,
  RomResponseDTO,
  RomUploadResponse,
} from '@shared/index.js';

class APIService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.clearToken();
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  private clearToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  // ==================== Auth Endpoints ====================

  async register(data: UserCreateDTO): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/register', data);
    this.setToken(response.data.token);
    return response.data;
  }

  async login(data: UserLoginDTO): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', data);
    this.setToken(response.data.token);
    return response.data;
  }

  async getMe(): Promise<{ user: UserResponseDTO }> {
    const response = await this.api.get<{ user: UserResponseDTO }>('/auth/me');
    return response.data;
  }

  logout(): void {
    this.clearToken();
  }

  // ==================== Save Endpoints ====================

  async getSaves(): Promise<SaveListResponseDTO> {
    const response = await this.api.get<SaveListResponseDTO>('/saves');
    return response.data;
  }

  async getSaveByRomHash(romHash: string, slot = 0): Promise<SaveResponseDTO> {
    const response = await this.api.get<SaveResponseDTO>(`/saves/${romHash}`, {
      params: { slot },
    });
    return response.data;
  }

  async uploadSave(
    romHash: string,
    saveData: Uint8Array,
    romName?: string,
    saveSlot = 0
  ): Promise<SaveResponseDTO> {
    const formData = new FormData();
    // Create a proper ArrayBuffer from Uint8Array
    const arrayBuffer = saveData.buffer.slice(saveData.byteOffset, saveData.byteOffset + saveData.byteLength) as ArrayBuffer;
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    formData.append('saveFile', blob, 'save.sav');
    formData.append('romHash', romHash);
    formData.append('saveSlot', saveSlot.toString());
    if (romName) {
      formData.append('romName', romName);
    }

    const response = await this.api.post<SaveResponseDTO>('/saves', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async downloadSave(saveId: number): Promise<ArrayBuffer> {
    const response = await this.api.get<ArrayBuffer>(`/saves/${saveId}/download`, {
      responseType: 'arraybuffer',
    });
    return response.data;
  }

  async deleteSave(saveId: number): Promise<void> {
    await this.api.delete(`/saves/${saveId}`);
  }

  // ==================== ROM Endpoints ====================

  async getUserRom(): Promise<RomResponseDTO | null> {
    try {
      const response = await this.api.get<RomResponseDTO>('/roms/user');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async uploadRom(
    romFile: File,
    romHash: string,
    romName: string,
    gameCode?: string,
    makerCode?: string,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<RomUploadResponse> {
    const formData = new FormData();
    formData.append('romFile', romFile);
    formData.append('romHash', romHash);
    formData.append('romName', romName);
    if (gameCode) {
      formData.append('gameCode', gameCode);
    }
    if (makerCode) {
      formData.append('makerCode', makerCode);
    }

    const response = await this.api.post<RomUploadResponse>(
      '/roms/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    );
    return response.data;
  }

  async downloadRom(): Promise<ArrayBuffer> {
    const response = await this.api.get<ArrayBuffer>('/roms/download', {
      responseType: 'arraybuffer',
    });
    return response.data;
  }

  async deleteRom(): Promise<void> {
    await this.api.delete('/roms');
  }

  // ==================== Health Check ====================

  async healthCheck(): Promise<{ status: string; message: string }> {
    const response = await this.api.get<{ status: string; message: string }>('/health');
    return response.data;
  }
}

export const apiService = new APIService();
